/**
 * The typed client for /api/v1 (the OpenAPI document's server). Every path the app calls
 * is here; the shapes come from the generated `types.ts`.
 */
import type {
  EstatePage,
  UnlockAllowance,
  UnlockResult,
  AccountCreate,
  AccountUpdate,
  AdminAccount,
  AgentSettings,
  AgentSettingsUpdate,
  CallSession,
  EmailLinkSent,
  County,
  LetterTemplate,
  LetterTemplateKey,
  LetterTemplates,
  LetterTemplateUpdate,
  MailRunLetters,
  DropRequest,
  LeadDetail,
  LeadPage,
  MailRun,
  MailSentRequest,
  MailSentResult,
  PasswordLink,
  PlanChoice,
  PlanProof,
  CheckoutRequest,
  CheckoutStarted,
  CheckoutOutcome,
  BillingSummary,
  PortalLink,
  PlanRequest,
  PricingCatalogue,
  Readiness,
  RegisteredCounty,
  SessionInfo,
  SignedOut,
  Stats,
  Records,
  TimezoneSetting,
  Today,
  TodayListKey,
  TodayPage,
  TouchCreate,
  RecoveryCodes,
  TwoStepChallenge,
  TwoStepSetup,
  TwoStepState,
} from "./models";
import type { EstatesQuery } from "@/lib/estates-query";
import { type LeadsQuery, PAGE_SIZE, apiParams, exportParams } from "@/lib/leads-query";

export const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** The API answers every error as {"error": {"code", "message"}}. */
function errorOf(body: unknown, status: number): ApiError {
  if (body && typeof body === "object" && "error" in body) {
    const error = body.error;
    if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
      const code = "code" in error && typeof error.code === "string" ? error.code : "http_error";
      return new ApiError(status, code, error.message);
    }
  }
  return new ApiError(status, "http_error", `The workspace answered ${status}.`);
}

function parseJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * The session's CSRF token, sent back on every request that changes something. The session
 * cookie itself is HttpOnly: the app never reads it, the browser sends it.
 */
let csrfToken = "";
/** Called when the server says nobody is signed in, so the app can go to the sign-in screen. */
let onSignedOut: (() => void) | null = null;

export function setCsrfToken(token: string) {
  csrfToken = token;
}

/**
 * Counts the sign-ins this page has seen. A write records the count when it is sent and
 * its answer touches the cache only if the count is unchanged, so an answer that arrives
 * after a sign-out never shows one account's records to the next.
 */
let signIns = 0;

export function signInChanged() {
  signIns += 1;
}

export function currentSignIn(): number {
  return signIns;
}

/** Stops a change sent in several requests when the sign-in it started under has ended, so
 * its later requests never go out under the next account's cookie. */
export function assertSignIn(signIn: number) {
  if (signIn !== signIns) throw new ApiError(401, "unauthenticated", "You were signed out; the rest was not saved.");
}

export function whenSignedOut(handler: (() => void) | null) {
  onSignedOut = handler;
}

/** How a request reaches the API: the network, or the sample workspace running in the page. */
type Transport = (url: string, init: RequestInit) => Promise<Response>;
let transport: Transport = (url, init) => fetch(url, init);
let demo = false;

/** Answers every request from the sample workspace instead of the server, for the rest of this page's life. */
export function startDemo(next: Transport) {
  transport = next;
  demo = true;
}

/** Whether this page shows the sample workspace, whose requests never leave the browser. */
export function inDemo(): boolean {
  return demo;
}

/** The routes that answer without a session; their 401 is an answer, not a lost session. */
const SESSION_PATHS = ["/session", "/session/two-step", "/account/password-link", "/account/sign-up", "/account/password-reset", "/plans"];

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    const headers = new Headers(init?.headers);
    headers.set("Accept", "application/json");
    if (init?.body) headers.set("Content-Type", "application/json");
    if (init?.method && init.method !== "GET") headers.set("X-CSRF-Token", csrfToken);
    response = await transport(`${API_BASE}${path}`, { ...init, headers, credentials: "same-origin" });
  } catch {
    throw new ApiError(0, "network", "The workspace is not answering. Is it running?");
  }
  const body = parseJson(await response.text());
  if (!response.ok) {
    const error = errorOf(body, response.status);
    if (response.status === 401 && !SESSION_PATHS.includes(path)) onSignedOut?.();
    throw error;
  }
  return body as T;
}

const send = <T>(method: "POST" | "PUT" | "DELETE", path: string, body: unknown = {}) =>
  request<T>(path, { method, body: JSON.stringify(body) });

const pageQuery = (page: number, pageSize?: number) =>
  `page=${page}${pageSize ? `&page_size=${pageSize}` : ""}`;
const lead = (id: string) => `/leads/${encodeURIComponent(id)}`;

export const api = {
  /** The signed-in account and its CSRF token; 401 when nobody is signed in. */
  session: () => request<SessionInfo>("/session"),
  /** A session, or the challenge an account with two-step sign-in passes with its code. */
  signIn: (email: string, password: string) => send<SessionInfo | TwoStepChallenge>("POST", "/session", { email, password }),
  /** The authenticator's code, or a recovery code, for a sign-in's challenge. */
  passTwoStep: (ticket: string, code: string) => send<SessionInfo>("POST", "/session/two-step", { ticket, code }),
  twoStep: () => request<TwoStepState>("/account/two-step"),
  beginTwoStep: () => send<TwoStepSetup>("POST", "/account/two-step"),
  confirmTwoStep: (code: string) => send<RecoveryCodes>("POST", "/account/two-step/confirm", { code }),
  renewRecoveryCodes: (code: string) => send<RecoveryCodes>("POST", "/account/two-step/recovery-codes", { code }),
  turnOffTwoStep: (password: string, code: string) => send<TwoStepState>("POST", "/account/two-step/off", { password, code }),
  signOut: () => send<SignedOut>("DELETE", "/session"),
  /** `chosen` is the counties the pricing page sent, kept by a new account. */
  signUp: (email: string, website: string, chosen: Partial<PlanRequest> = {}) =>
    send<EmailLinkSent>("POST", "/account/sign-up", { email, website, ...chosen }),
  plans: () => request<PricingCatalogue>("/plans"),
  planChoice: () => request<PlanChoice | null>("/account/plan"),
  requestPlan: (body: PlanRequest) => send<PlanChoice>("PUT", "/account/plan", body),
  /** Stripe's checkout page for these counties' plan. */
  startCheckout: (body: CheckoutRequest) => send<CheckoutStarted>("POST", "/account/checkout", body),
  /** What became of a checkout; a paid one has opened its counties by the time this answers. */
  checkout: (sessionId: string) => request<CheckoutOutcome>(`/account/checkout/${encodeURIComponent(sessionId)}`),
  billing: () => request<BillingSummary | null>("/account/billing"),
  billingPortal: () => send<PortalLink>("POST", "/account/billing-portal"),
  /** What these counties' subscription brings, read before paying. */
  proof: (counties: string[]) =>
    request<PlanProof>(`/proof?${counties.map((county) => `county=${encodeURIComponent(county)}`).join("&")}`),
  requestPasswordReset: (email: string, website: string) => send<EmailLinkSent>("POST", "/account/password-reset", { email, website }),
  setPassword: (token: string, password: string) =>
    send<SessionInfo | TwoStepChallenge>("POST", "/account/password-link", { token, password }),
  changePassword: (current_password: string, new_password: string) =>
    send<SessionInfo>("PUT", "/account/password", { current_password, new_password }),
  accounts: () => request<AdminAccount[]>("/admin/accounts"),
  registeredCounties: () => request<RegisteredCounty[]>("/admin/counties"),
  createAccount: (body: AccountCreate) => send<PasswordLink>("POST", "/admin/accounts", body),
  updateAccount: (id: string, body: AccountUpdate) => send<AdminAccount>("PUT", `/admin/accounts/${encodeURIComponent(id)}`, body),
  passwordLink: (id: string) => send<PasswordLink>("POST", `/admin/accounts/${encodeURIComponent(id)}/password-link`),
  /** `limit` caps the rows of each list; the counts stay whole. */
  today: (limit?: number) => request<Today>(`/today${limit ? `?limit=${limit}` : ""}`),
  /** One Today list, a page at a time. */
  todayList: (key: TodayListKey, page: number, pageSize?: number) =>
    request<TodayPage>(`/today/${encodeURIComponent(key)}?${pageQuery(page, pageSize)}`),
  leads: (query: LeadsQuery) => request<LeadPage>(`/leads?${apiParams(query)}`),
  lead: (id: string) => request<LeadDetail>(lead(id)),
  logTouch: (id: string, body: TouchCreate) => send<LeadDetail>("POST", `${lead(id)}/touches`, body),
  drop: (id: string, body: DropRequest = {}) => send<LeadDetail>("POST", `${lead(id)}/drop`, body),
  undrop: (id: string) => send<LeadDetail>("POST", `${lead(id)}/undrop`),
  addNote: (id: string, text: string) => send<LeadDetail>("POST", `${lead(id)}/notes`, { text }),
  acknowledgeApproval: (id: string) => send<LeadDetail>("POST", `${lead(id)}/approval-acknowledged`),
  /** The estates the agent has not unlocked, the family's details held back. */
  estates: (query: EstatesQuery) => request<EstatePage>(`/estates?${estateParams(query)}`),
  unlocks: () => request<UnlockAllowance>("/unlocks"),
  unlockEstate: (estateId: string) => send<UnlockResult>("POST", `/estates/${encodeURIComponent(estateId)}/unlock`),
  mailRun: (page = 1) => request<MailRun>(`/mail-run?${pageQuery(page)}`),
  markSent: (body: MailSentRequest) => send<MailSentResult>("POST", "/mail-run/sent", body),
  callSession: () => request<CallSession>("/call-session"),
  records: (id: string) => request<Records>(`${lead(id)}/records`),
  readiness: () => request<Readiness>("/readiness"),
  stats: () => request<Stats>("/stats"),
  counties: () => request<County[]>("/counties"),
  /** Replaces the counties the agent works; every list follows. */
  saveCounties: (counties: string[]) => send<County[]>("PUT", "/settings/counties", { counties }),
  setTimezone: (timezone: string) => send<TimezoneSetting>("PUT", "/settings/timezone", { timezone }),
  /** The merged letters of the named leads, or the first page of the letters due. */
  mailLetters: (leadIds: string[] = []) => request<MailRunLetters>(`/mail-run/letters${leadQuery(leadIds)}`),
  letterTemplates: () => request<LetterTemplates>("/letter-templates"),
  saveLetterTemplate: (key: LetterTemplateKey, body: LetterTemplateUpdate) =>
    send<LetterTemplate>("PUT", `/letter-templates/${encodeURIComponent(key)}`, body),
  agent: () => request<AgentSettings>("/settings/agent"),
  saveAgent: (body: AgentSettingsUpdate) => send<AgentSettings>("PUT", "/settings/agent", body),
};

function estateParams(query: EstatesQuery): string {
  const params = new URLSearchParams({ page: String(query.page), page_size: String(PAGE_SIZE), sort: query.sort });
  if (query.county) params.set("county", query.county);
  if (query.approved) params.set("approved", "true");
  if (query.has_property) params.set("has_property", "true");
  return params.toString();
}

function leadQuery(leadIds: string[]): string {
  const params = new URLSearchParams();
  for (const id of leadIds) params.append("lead_id", id);
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** The print page of the app: the letters of the named leads, or every letter due. */
export function printLettersHref(leadIds: string[] = []): string {
  return `/letters/print${leadQuery(leadIds)}`;
}

/** The Leads export: the current filters, every page, as the mail-run CSV. */
export function leadsExportHref(query: LeadsQuery): string {
  const params = exportParams(query).toString();
  return `${API_BASE}/leads/export.csv${params ? `?${params}` : ""}`;
}

/** The mail-run export: the chosen letters, or every letter due when none are chosen. */
export function mailRunExportHref(leadIds: string[] = []): string {
  return `${API_BASE}/mail-run/export.csv${leadQuery(leadIds)}`;
}


/** Fields this build of the app relies on; an older server lacks them and pages would break. */
const REQUIRED_FIELDS: Record<string, string[]> = {
  LeadSummary: ["next_step_label", "contact_state", "house_status", "house_status_label", "timeline", "days_since_approval"],
  LeadDetail: ["date", "next_step_label", "house_status", "timeline"],
  Today: ["order"],
  AgentSettings: ["onboarding_seen"],
  Stats: ["outcomes"],
  OutcomeGroup: ["talked_share"],
};

/**
 * Names the fields the running server does not provide, or an empty list when it is current.
 * A server that offers no OpenAPI document (the test mock) is taken as current.
 */
export async function checkContract(): Promise<string[]> {
  type OpenApiDoc = { components?: { schemas?: Record<string, { properties?: Record<string, unknown> }> } };
  let doc: OpenApiDoc | null;
  if (demo) return [];
  try {
    const response = await fetch(`${API_BASE}/openapi.json`, { headers: { Accept: "application/json" } });
    if (!response.ok) return [];
    doc = parseJson(await response.text()) as OpenApiDoc | null;
  } catch {
    return [];
  }
  const schemas = doc?.components?.schemas;
  if (!schemas) return [];
  const missing: string[] = [];
  for (const [schema, fields] of Object.entries(REQUIRED_FIELDS)) {
    const properties = schemas[schema]?.properties;
    if (!properties) continue;
    for (const field of fields) if (!(field in properties)) missing.push(`${schema}.${field}`);
  }
  return missing;
}
